import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';

const DEFAULTS = {
  accent: 'blue',
  density: 'cozy',
  zoom: 100,
  theme: 'system',
  status_emoji: '',
  status_text: '',
  pinned_page_ids: [],
  sidebar_collapsed: false,
  language_style: 'standard',
  notification_prefs: {
    nudge_email: true,
    digest_daily: true,
    digest_wednesday: true,
    toast_muted_low_priority: false,
  },
  reminder_times: {
    timesheet_end_of_day_hhmm: '17:30',
    commitment_morning_hhmm: '09:15',
  },
  layout_prefs: {
    home_focus: 'commitments',
  },
};

const PrefsCtx = createContext(null);

export const ACCENTS = [
  { id: 'blue', label: 'Slate blue', hex: '#2264d1' },
  { id: 'violet', label: 'Violet', hex: '#7c3aed' },
  { id: 'emerald', label: 'Emerald', hex: '#059669' },
  { id: 'amber', label: 'Amber', hex: '#c2751a' },
  { id: 'rose', label: 'Rose', hex: '#e11d48' },
  { id: 'slate', label: 'Ink slate', hex: '#334155' },
];

export const DENSITIES = [
  { id: 'compact', label: 'Compact' },
  { id: 'cozy', label: 'Cozy' },
  { id: 'comfortable', label: 'Comfortable' },
];

function resolveTheme(pref) {
  if (pref === 'dark' || pref === 'light') return pref;
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

function mergeDeep(base, override) {
  if (!override) return base;
  const out = { ...base };
  Object.keys(override).forEach(k => {
    const v = override[k];
    if (v && typeof v === 'object' && !Array.isArray(v) && base[k] && typeof base[k] === 'object') {
      out[k] = { ...base[k], ...v };
    } else if (v !== undefined) {
      out[k] = v;
    }
  });
  return out;
}

export function PrefsProvider({ children }) {
  const { user, token } = useAuth();
  const [prefs, setPrefsState] = useState(() => {
    try {
      const cached = localStorage.getItem('32d.os.prefs');
      if (cached) return mergeDeep(DEFAULTS, JSON.parse(cached));
    } catch { /* ignore */ }
    return DEFAULTS;
  });

  // ---- Simple language string-table (fetched once, updates on language switch)
  const [strings, setStrings] = useState({ style: 'standard', all: {} });

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-accent', prefs.accent || 'blue');
    root.setAttribute('data-density', prefs.density || 'cozy');
    root.setAttribute('data-theme', resolveTheme(prefs.theme));
    root.setAttribute('data-language-style', prefs.language_style || 'standard');
    root.style.setProperty('--zoom', String((prefs.zoom || 100) / 100));
    try { localStorage.setItem('32d.os.prefs', JSON.stringify(prefs)); } catch { /* ignore */ }
  }, [prefs]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return () => {};
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      if ((prefs.theme || 'system') === 'system') {
        document.documentElement.setAttribute('data-theme', mq.matches ? 'dark' : 'light');
      }
    };
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, [prefs.theme]);

  useEffect(() => {
    if (!user || !token) return;
    api.get('/prefs').then(r => setPrefsState(p => mergeDeep(mergeDeep(DEFAULTS, p), r.data))).catch(() => {});
  }, [user?.id, token]);

  // Fetch string table when language_style changes
  useEffect(() => {
    if (!user || !token) return;
    api.get('/prefs/strings').then(r => setStrings(r.data)).catch(() => {});
  }, [user?.id, token, prefs.language_style]);

  const patch = useCallback(async (partial) => {
    setPrefsState(p => mergeDeep(p, partial));
    try { await api.patch('/prefs', partial); } catch { /* ignore */ }
  }, []);

  const togglePin = useCallback(async (pageId, pinned) => {
    setPrefsState(p => ({
      ...p,
      pinned_page_ids: pinned
        ? Array.from(new Set([...(p.pinned_page_ids || []), pageId]))
        : (p.pinned_page_ids || []).filter(x => x !== pageId),
    }));
    try { await api.post('/prefs/pin', { page_id: pageId, pinned }); } catch { /* ignore */ }
  }, []);

  const value = useMemo(() => ({ prefs, patch, togglePin, strings }), [prefs, patch, togglePin, strings]);
  return <PrefsCtx.Provider value={value}>{children}</PrefsCtx.Provider>;
}

export const usePrefs = () => useContext(PrefsCtx) || { prefs: DEFAULTS, patch: () => {}, togglePin: () => {}, strings: { style: 'standard', all: {} } };

/**
 * useSimpleLabel — return a translator for a given namespace.
 *
 * Usage:
 *   const t = useSimpleLabel('timesheet');
 *   <h1>{t('page_title', 'Timesheets')}</h1>
 *
 * The fallback is used both when the string-table hasn't loaded yet AND
 * when the caller uses a key that doesn't exist. This keeps the app safe
 * to render even if the /prefs/strings call fails.
 */
export function useSimpleLabel(namespace) {
  const { strings } = usePrefs();
  return useCallback((key, fallback) => {
    const table = strings?.all?.[namespace];
    const v = table && table[key];
    return (v !== undefined && v !== null && v !== '') ? v : (fallback ?? key);
  }, [strings, namespace]);
}
